
<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Automations">
      <router-link :to="{ name: 'Monitor' }" class="mr-2 btn btn-secondary" target="_blank" v-if="account.subscription.monitor">
        Monitor Dashboard <fa :icon="['far', 'external-link']" class="ml-2" />
      </router-link>
      <button class="btn btn-primary" @click="showAutomationsModal = true">
        Add Automation
      </button>
    </PageHeader>

    <Modal :open.sync="showAutomationsModal" title="Add Automation" size="sm:max-w-3xl">
      <div class="grid grid-cols-2 px-4 pb-4 gap-x-4">
        <div v-for="(auto, key) in availableAutomationTypes" :key="key">
          <router-link :to="{ name: 'AutomationCreate', params: { type: key, account: $route.params.account } }" class="flex items-center p-3 font-medium text-gray-800 rounded hover:bg-gray-100 hover:no-underline">
            <fa :icon="['fas', auto.icon]" fixed-width class="mr-3 text-gray-500" /> {{ auto.name }} 
          </router-link>
        </div>
      </div>
    </Modal>

    <div class="container">      
      <!-- List -->
      <div>
        <div class="flex flex-col justify-between pt-6 mb-6 space-y-3 border-t md:space-y-0 md:flex-row">
          <FilterDropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
            <div>
              <label for="type" class="form-label">Type</label>
              <AutomationTypeSelect type="type" v-model="filter.type" />
            </div>
          </FilterDropdown>

          <ButtonToggle
            :enabled="account.automations_enabled"
            :status-text="account.automations_enabled ? 'Automations are Running' : 'Automations are Paused'"
            :button-text="account.automations_enabled ? 'Disable Automations' : 'Enable Automations'" 
            :updating="submitting"
            @toggle="toggleAutomations" />
        </div>

        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="automations.meta.total < 1 && !$route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No automations setup yet.
          </div>
          <div class="text-gray-500">
            Automations can be configured to watch your fleet for certain triggers and then dispatch actions like sending emails, SMSs, or driver messages.
          </div>
          <button class="mt-4 btn btn-outline" @click="showAutomationsModal = true">
            Add Automation
          </button>
        </div>

        <!-- No Results -->
        <div v-else-if="automations.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No automations matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see all automations.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Listing -->
        <div class="grid gap-4 xl:grid-cols-2" v-else>
          <div v-for="automation in automations.data" :key="automation.id">
            <div class="block transition-shadow card hover:shadow">
              <div class="grid items-center justify-between grid-cols-4 px-4 py-4 lg:px-5">
                <!-- Left -->
                <router-link :to="{ name: 'AutomationEdit', params: { id: automation.id } }" class="flex items-center flex-grow col-span-3 text-gray-900 hover:no-underline">
                  <!-- Icon -->
                  <div class="flex items-center justify-center flex-none w-10 h-10 mr-4 bg-gray-100 rounded-md">
                    <fa :icon="['far', automationTypes[automation.type].icon]" 
                      fixed-width class="text-lg text-gray-500" /> 
                  </div>
                  <!-- Name -->
                  <div class="w-full pr-12 leading-tight">
                    <div class="font-bold truncate">
                      {{ automation.name || '-' }}
                    </div>
                    <div class="text-gray-500 truncate">
                      {{ automationTypes[automation.type].name }} 
                    </div>
                  </div>
                </router-link>
                <!-- Right -->
                <div class="flex items-center justify-end col-span-1">
                  <!-- Toggle -->
                  <toggle 
                    class="mr-4"
                    :value.sync="automation.enabled" 
                    v-tooltip="automation.enabled ? `Enabled` : `Disabled`"
                    @change="toggleAutomation(automation)" /> 
                  <!-- Dropdown -->
                  <dropdown>
                    <div class="grid w-8 h-8 leading-none text-gray-500 rounded place-items-center hover:bg-gray-100">
                      <fa :icon="['far', 'chevron-down']" class="text-sm" />
                    </div>
                    <div slot="dropdown" class="py-2 bg-white border rounded">
                      <router-link :to="{ name: 'AutomationEdit', params: { id: automation.id } }" class="flex items-center w-full px-4 py-2 font-medium text-left text-gray-800 hover:no-underline hover:bg-gray-50">
                        <fa :icon="['far', 'sliders-v']" fixed-width class="mr-3 opacity-50" />
                        Edit
                      </router-link>
                      <button class="flex items-center w-full px-4 py-2 font-medium text-left hover:bg-gray-50" @click="duplicateAutomation(automation)">
                        <fa :icon="['far', 'clone']" fixed-width class="mr-3 opacity-50" />
                        Duplicate
                      </button>
                      <button class="flex items-center w-full px-4 py-2 font-medium text-left hover:bg-gray-50" @click="deleteAutomation(automation)">
                        <fa :icon="['far', 'trash-alt']" fixed-width class="mr-3 opacity-50" />
                        Delete
                      </button>
                    </div>
                  </dropdown>
                </div>
              </div>
              <div class="flex flex-col items-center justify-between px-4 py-3 rounded-b-lg lg:py-1 lg:px-5 md:flex-row bg-gray-50">
                <!-- Options -->
                <div class="grid items-center grid-flow-col gap-1 text-sm font-medium text-gray-500">
                  <div>
                    {{ automation.vehicles_count || 'No' }} Vehicles
                  </div>
                  <template v-if="automationTypes[automation.type].threshold()">
                    <div class="mx-1">
                      &bullet;
                    </div>
                    <div>
                      {{ automationTypes[automation.type].threshold(automation.threshold) }} 
                    </div>
                  </template>
                  <template v-if="automationTypes[automation.type].condition()">
                    <div class="mx-1">
                      &bullet;
                    </div>
                    <div>
                      {{ automationTypes[automation.type].condition(automation.condition) }} 
                    </div>
                  </template>
                </div>
                <!-- Actions -->
                <div class="flex items-center h-8 mt-1 overflow-hidden rounded-full lg:mt-0">
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.cellphones_count >= 1"
                    v-tooltip="automation.cellphones_count > 0 ? `${automation.cellphones_count} contact${automation.cellphones_count > 1 ? 's' : ''} will be sent an SMS.` : ''">
                    <fa :icon="['far', 'mobile-android']" />
                  </div>
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.emails_count >= 1"
                    v-tooltip="automation.emails_count > 0 ? `${automation.emails_count} contact${automation.emails_count > 1 ? 's' : ''} will be sent an email.` : ''">
                    <fa :icon="['far', 'envelope']" />
                  </div>
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.notify_driver"
                    v-tooltip="automation.notify_driver ? `Driver will be sent a message.` : ''">
                    <fa :icon="['far', 'comment-alt-dots']" />
                  </div>
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.activate_output"
                    v-tooltip="automation.activate_output ? `Output will be activated when triggered.` : ''">
                    <fa :icon="['far', 'toggle-on']" />
                  </div>
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.deactivate_output"
                    v-tooltip="automation.deactivate_output ? `Output will be deactivated when triggered.` : ''">
                    <fa :icon="['far', 'toggle-off']" />
                  </div>
                  <div 
                    class="flex items-center justify-center w-8 text-gray-500 h-7" 
                    v-if="automation.trigger_dashboard_alert"
                    v-tooltip="automation.trigger_dashboard_alert ? `A monitor dashboard alert will be sent.` : ''">
                    <fa :icon="['far', 'exclamation-circle']" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Pagination -->
      <Pagination v-if="automations.meta" transparent class="mt-4" :total="automations.meta.total" :per-page="automations.meta.per_page" :current-page="automations.meta.current_page" />
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import FilterDropdown from '@/components/FilterDropdown'
import AutomationTypeSelect from '@/components/AutomationTypeSelect'
import ButtonToggle from '@/components/ButtonToggle'

export default {
  name: 'AutomationList',

  metaInfo: {
    title: 'Automations'
  },

  components: {
    FilterDropdown,
    AutomationTypeSelect,
    ButtonToggle
  },

  created () {
    this.getAutomations()
  },

  computed: {
    ...mapState(['automationTypes']),
    ...mapGetters(['account']),

    availableAutomationTypes () {
      return Object.fromEntries(Object.entries(this.automationTypes).filter(([key, value]) => key !== 'area-change'))
    }
  },

  data () {
    return {
      showAutomationsModal: false,
      loading: true,
      submitting: false,
      automations: {},
      filter: {
        type: null
      }
    }
  },

  methods: {
    async getAutomations () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/automations`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'type',
            dir: this.$route.query.dir || 'asc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.automations = data.automations
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        type: null
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    async toggleAutomation (automation) {
      try {
        const post = {
          ...automation,
          enabled: automation.enabled
        }
        const { data } = await this.$api.patch(`account/${this.$route.params.account}/automations/${automation.id}`, post)
      } catch (error) {
        console.error(error)
      }
    },

    async toggleAutomations () {
      const switchTo = !this.account.automations_enabled

      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/accounts/${this.account.id}`, {
          ...this.account,
          automations_enabled: switchTo
        })
        await this.$store.dispatch('syncUser', this)
        this.$toasted.show(`All automations ${switchTo ? 'enabled' : 'disabled'}.`)
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    async deleteAutomation (automation) {
      if (window.confirm('Are you sure you want to delete this automation?')) {
        try {
          const index = this.automations.data.findIndex((a) => { return a.id === automation.id})
          await this.$api.delete(`account/${this.$route.params.account}/automations/${automation.id}`)
          this.$delete(this.automations.data, index)
          this.$toasted.show('Automation deleted.')
        } catch (error) {
          console.error(error)
        } finally {
          this.submitting = false
        }
      }
    },

    async duplicateAutomation (automation) {
      try {
        const url = `account/${this.$route.params.account}/automations/${automation.id}`
        const { data: original } = await this.$api.get(url)
        const { data: duplicate } = await this.$api.post(`account/${this.$route.params.account}/automations`, { 
          ...original.automation, 
          name: `Copy of ${original.name || 'Unnamed Automation'}`,
          enabled: false 
        })
        this.$router.push({ name: 'AutomationEdit', params: { id: duplicate.automation.id }})
        this.$toasted.show('Automation duplicated.')
      } catch (error) {
        console.error(error)
      } finally {
        this.submitting = false
      }
    },
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getAutomations()
      },
      deep: true
    }
  }
}
</script>